import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'
import type { StartMode } from './types'

/**
 * Ход «Открыть смену», пока им занят САМ клиент (RP-FB22 3): проверка связи, затем работа за кассой и команда
 * смены. Когда команда принята, правду о ходе говорит операция на сервере (ждём ответ кассы) — запись снимается.
 * Срок — `screen`: после F5 ход восстанавливается по `sessions/mine` и статусу команды, а не по памяти вкладки.
 */
export type OpeningPhase = 'link' | 'send'

export interface OpeningEntry {
  deviceId: number
  mode: StartMode
  registerName: string
  cashierName: string | null
  phase: OpeningPhase
  /** ISO — отсчёт времени на экране ожидания. */
  startedAt: string
}

export interface OpeningState {
  byResource: Record<string, OpeningEntry>
}

const initialState: OpeningState = { byResource: {} }

const keyOf = (resourceId: string) => ridKey(resourceId, 'opening')

const openingSlice = createSlice({
  name: 'aqsi/opening',
  initialState,
  reducers: {
    openingStarted(state, action: PayloadAction<{ resourceId: string } & Omit<OpeningEntry, 'phase'>>) {
      const { resourceId, ...entry } = action.payload
      state.byResource[keyOf(resourceId)] = { ...entry, phase: 'link' }
    },
    openingPhaseChanged(state, action: PayloadAction<{ resourceId: string; phase: OpeningPhase }>) {
      const entry = state.byResource[keyOf(action.payload.resourceId)]
      if (entry !== undefined) entry.phase = action.payload.phase
    },
    openingFinished(state, action: PayloadAction<{ resourceId: string }>) {
      delete state.byResource[keyOf(action.payload.resourceId)]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.byResource, action.payload.resourceId))
  },
})

export const { openingStarted, openingPhaseChanged, openingFinished } = openingSlice.actions
export const openingReducer = openingSlice.reducer

export function openingOf(state: OpeningState, resourceId: string): OpeningEntry | undefined {
  return state.byResource[keyOf(resourceId)]
}
